import React from "react";
import { Indicator, Tabs } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { humanize, type FieldGroup } from "@utils/mappingConfig";

export interface ExtraTab {
  value: string;
  label: React.ReactNode;
}

export interface ConfigGroupTabsProps {
  groups: FieldGroup[];
  value: string;
  onChange: (next: string) => void;
  /** Tabs after the config groups (e.g. History, Drafts), set apart by a gap. */
  extraTabs?: ExtraTab[];
  /** Groups holding unsaved edits: flagged orange. */
  changed?: Set<string>;
  /** Groups holding an error: flagged red (wins over `changed`). */
  problems?: Set<string>;
}

/**
 * One tab per config group of a service's schema, so each part of a config
 * (LLM, retrieval, QA template, ...) is its own page instead of one long form.
 * Only the tab list: the caller renders the content for the selected value.
 */
export const ConfigGroupTabs: React.FC<ConfigGroupTabsProps> = ({
  groups, value, onChange, extraTabs = [], changed, problems,
}) => {
  const { t } = useTranslation();
  return (
    <Tabs value={value} onChange={(next) => next && onChange(next)} keepMounted={false}>
      <Tabs.List>
        {groups.map(({ group }) => {
          const color = problems?.has(group) ? "red" : changed?.has(group) ? "orange" : undefined;
          const label = t(`mappingConfig.groups.${group}`, { defaultValue: humanize(group) });
          return (
            <Tabs.Tab key={group} value={group} data-group-tab={group} data-flag={color ?? undefined}>
              {color ? (
                <Indicator color={color} size={8} offset={-4} position="top-end" processing={false}>
                  {label}
                </Indicator>
              ) : label}
            </Tabs.Tab>
          );
        })}
        {extraTabs.map((tab, i) => (
          <Tabs.Tab key={tab.value} value={tab.value} ml={i === 0 ? "auto" : undefined}>
            {tab.label}
          </Tabs.Tab>
        ))}
      </Tabs.List>
    </Tabs>
  );
};
